import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  fetchCommunities, 
  setSearchFilter, 
  setSelectedCategory,
  clearFilters 
} from '../store/slices/communitySlice';
import './Communities.scss';

export const Communities = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { 
    communities, 
    loading, 
    error,
    pagination,
    filters 
  } = useSelector((state) => state.community);

  const [categories, setCategories] = useState([
    { _id: '', name: 'All Communities', icon: '🌐' }
  ]);

  useEffect(() => {
    // Fetch categories from API using axios
    const fetchCategories = async () => {
      try {
        const res = await axios.get(('https://yoospeak.app:5000') + '/categories');
        const data = res.data;
        if (data && data.data) {
          // Try to extract emoji from name if present
          const apiCategories = data.data.map(cat => {
            const match = cat.name.match(/(.+?)([\u{1F300}-\u{1FAFF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}])?$/u);
            return {
              _id: cat._id,
              name: match ? match[1].trim() : cat.name,
              icon: match && match[2] ? match[2] : '',
            };
          });
          setCategories([{ _id: '', name: 'All Communities', icon: '🌐' }, ...apiCategories]);
        }
      } catch (err) {
        // Optionally handle error
        setCategories([{ _id: '', name: 'All Communities', icon: '🌐' }]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    loadCommunities(1);
  }, []);

  const loadCommunities = (page = 1) => {
    const params = {
      page,
      limit: 20,
      search: filters.search,
      type: filters.type,
      category: filters.selectedCategory,
    };
    dispatch(fetchCommunities(params));
  };

  const handleSearch = (e) => {
    dispatch(setSearchFilter(e.target.value));
    // Debounce search
    setTimeout(() => {
      loadCommunities(1);
    }, 500);
  };

  const handleCategoryClick = (categoryId) => {
    dispatch(setSelectedCategory(categoryId));
  };

  useEffect(() => {
    loadCommunities(1);
    // eslint-disable-next-line
  }, [filters.selectedCategory]);

  const handlePageChange = (newPage) => {
    loadCommunities(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCommunityClick = (communityId) => {
    navigate(`/get-community/${communityId}`);
  };

  return (
    <div className="communities-page">
      {/* Hero Section */}
      <section className="hero-section text-center py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 text-lg-start mb-4 mb-lg-0">
              <h1 className="display-4 fw-bold mb-3">
                Discover <span className="text-primary">communities</span>
              </h1>
              <p className="lead text-muted mb-4">
                Join vibrant communities where you can learn, share, and grow with like-minded individuals from around the world.
              </p>
              <button className="btn btn-primary btn-lg px-5 rounded-pill">
                Create Community
              </button>
            </div>
            <div className="col-lg-6">
              <img 
                src="https://via.placeholder.com/600x400/6366f1/ffffff?text=Community" 
                alt="Community" 
                className="img-fluid rounded-3"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Search & Category Section */}
      <section className="search-section py-4 bg-white shadow-sm">
        <div className="container">
          <div className="row mb-3">
            <div className="col-12">
              <div className="input-group input-group-lg">
                <span className="input-group-text bg-white border-end-0">
                  <i className="bi bi-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0 ps-0"
                  placeholder="Search for communities..."
                  value={filters.search}
                  onChange={handleSearch}
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="categories-section py-3 px-2 rounded-3 mb-2 d-flex flex-wrap justify-content-center gap-3">
                {categories.map((cat) => (
                  <div
                    key={cat._id}
                    className={`category-card px-4 py-2 d-flex align-items-center rounded-3 shadow-sm ${filters.selectedCategory === cat._id ? 'border-primary' : 'border'}`}
                    style={{ minWidth: 160, cursor: 'pointer', borderWidth: 2 }}
                    onClick={() => handleCategoryClick(cat._id)}
                  >
                    <span style={{ fontSize: '1.5rem', marginRight: 10 }}>{cat.icon}</span>
                    <span className="fw-semibold">{cat.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Communities Grid */}
      <section className="communities-grid py-5">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h3 className="fw-bold mb-0">
              {filters.selectedCategory === ''
                ? 'Best communities'
                : `${categories.find(c => c._id === filters.selectedCategory)?.name || ''} Communities`}
            </h3>
            <span className="text-muted">{pagination.total || 0} communities found</span>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : communities.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted fs-5">No communities found. Try a different search or category.</p>
            </div>
          ) : (
            <>
              <div className="row g-4">
                {communities.map((community) => (
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={community._id}>
                    <div className="community-card card h-100 border-0 shadow-sm" onClick={() => handleCommunityClick(community._id)} style={{ cursor: 'pointer' }}>
                      <div className="position-relative">
                        <img
                          src={community.image || 'https://via.placeholder.com/400x250/6366f1/ffffff?text=Community'}
                          className="card-img-top"
                          alt={community.name}
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <span className={`badge position-absolute top-0 end-0 m-2 ${
                          community.price > 0 ? 'bg-warning' : 'bg-success'
                        }`}>
                          {community.price > 0 ? `$${community.price}/mo` : 'Free'}
                        </span>
                      </div>
                      <div className="card-body">
                        <h5 className="card-title fw-bold mb-2">{community.name}</h5>
                        <p className="card-text text-muted small mb-3">
                          {community.description?.substring(0, 80)}
                          {community.description?.length > 80 ? '...' : ''}
                        </p>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center">
                            <i className="bi bi-people-fill text-primary me-2"></i>
                            <span className="small text-muted">
                              {community.members || 0} members
                            </span>
                          </div>
                          <button className="btn btn-sm btn-primary rounded-pill px-3" onClick={(e) => { e.stopPropagation(); }}>
                            Join
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="d-flex justify-content-center mt-5">
                  <nav aria-label="Communities pagination">
                    <ul className="pagination pagination-lg">
                      <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(pagination.page - 1)}
                          disabled={pagination.page === 1}
                        >
                          <i className="bi bi-chevron-left"></i>
                        </button>
                      </li>
                      
                      {[...Array(pagination.pages)].map((_, index) => {
                        const pageNum = index + 1;
                        // Show first page, last page, current page, and pages around current
                        if (
                          pageNum === 1 ||
                          pageNum === pagination.pages ||
                          (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
                        ) {
                          return (
                            <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </button>
                            </li>
                          );
                        } else if (
                          pageNum === pagination.page - 3 ||
                          pageNum === pagination.page + 3
                        ) {
                          return <li key={pageNum} className="page-item disabled"><span className="page-link">...</span></li>;
                        }
                        return null;
                      })}
                      
                      <li className={`page-item ${pagination.page === pagination.pages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => handlePageChange(pagination.page + 1)}
                          disabled={pagination.page === pagination.pages}
                        >
                          <i className="bi bi-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};
